import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus } from 'react-icons/fa';
import { GrSubtract } from 'react-icons/gr';

import '../Styles/style.css';

import { EnvService } from '../../../../../services/EnvService';
import { statusColorsKYC } from '../../utils';
import {
  normalizeDocumentType,
  ownerFromBelongsToShareholder
} from '../../../../../utils/shareholderPersonalDocs';

interface ShareholdersTableProps {
  shareholders: any[];
  isDelaware?: boolean;
}

const DetailField = ({
  label,
  value
}: {
  label: string;
  value?: React.ReactNode;
}) => (
  <div className="space-y-1">
    <label className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</label>
    <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm font-medium text-gray-800 capitalize">
      {value || '—'}
    </p>
  </div>
);

export const ShareholdersList: React.FC<ShareholdersTableProps> = ({ shareholders, isDelaware = false }) => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const envService = EnvService.MODULE_KYC_TOGGLE;
  const isVibileKYCModule = envService === 'true' ? true : false;
  const sharesLabel = isDelaware ? t('others.othNumberOfUnits') : t('others.othNumberOfShares');

  const handleClick = (index: number) => {
    setActiveIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const getPersonTypeBadge = (isDirector: boolean) => {
    if (isDirector) {
      return (
        <span className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800">
          {t('others.othDirectors')}
        </span>
      );
    }
    return (
      <span className="inline-flex rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
        {t('others.othShareholders')}
      </span>
    );
  };

  const getKYCStatusBadge = (status: string) => {
    const statusConfig = {
      approved: { bg: 'bg-green-100', text: 'text-green-800' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-800' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return (
      <span className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${config.bg} ${config.text}`}>
        {t(`KYCDocuments.statusKYC.${status}`)}
      </span>
    );
  };

  const formatPhone = (shareholder: any) => {
    const phone = shareholder?.telephones?.[0];
    if (!phone) return null;
    const ddi = phone.DDI || phone.ddi || '';
    const ddd = phone.DDD || phone.ddd || '';
    const number = phone.number || '';
    return [ddi && `+${ddi}`, ddd, number].filter(Boolean).join(' ');
  };

  const address = (shareholder: any) => shareholder?.address?.[0];
  const passport = (shareholder: any) => shareholder?.passports?.[0];

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4">
        <h2 className="mb-1 text-xl font-semibold text-gray-800">{t('others.otheShareholdersAndDirectors')}</h2>
      </div>

      <div className="p-6">
        {shareholders && shareholders.length > 0 ? (
          <div className="grid gap-4">
            {shareholders.map((shareholder, index) => {
              const proofOwner = ownerFromBelongsToShareholder(
                address(shareholder)?.belongs_to_shareholder,
                address(shareholder)?.meta_data?.address_proof_owner
              );
              const documentType = normalizeDocumentType(
                shareholder?.documents?.[0]?.document_type ||
                  shareholder?.documents?.[0]?.meta_data?.document_type
              );

              return (
              <div
                key={shareholder.id || index}
                className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="border-b border-gray-200 bg-white px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-500 font-semibold text-white">
                        {shareholder.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold capitalize text-gray-800">{shareholder.name}</h3>
                        <p className="text-sm capitalize text-gray-600">
                          {shareholder.occupation || t('labels.lblOccupation')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getPersonTypeBadge(!!shareholder.is_director)}
                      <button
                        type="button"
                        onClick={() => handleClick(index)}
                        className="rounded-lg p-2 transition-colors hover:bg-gray-200"
                        aria-label={t('others.othDetails') || 'Details'}
                      >
                        {activeIndex === index ? <GrSubtract /> : <FaPlus />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <DetailField
                      label={sharesLabel}
                      value={shareholder.number_of_shares?.toLocaleString?.() ?? shareholder.number_of_shares ?? '0'}
                    />
                    <DetailField label={t('labels.lblNationality')} value={shareholder.nationality} />
                    <DetailField label={t('labels.lblEmail')} value={shareholder.email} />
                    <DetailField label={t('labels.lblPhone')} value={formatPhone(shareholder)} />
                    <DetailField label={t('labels.lblDateOfBirth')} value={shareholder.date_of_birth} />
                    <DetailField label={t('labels.lblGender')} value={shareholder.gender} />
                    <DetailField
                      label={
                        documentType === 'drivers_license'
                          ? t('labels.lblDriverLicence')
                          : t('labels.lblNationalID')
                      }
                      value={shareholder.document_number}
                    />
                    {isVibileKYCModule ? (
                      <div className="space-y-1">
                        <label className="text-xs font-medium uppercase tracking-wide text-gray-500">Status KYC</label>
                        <div>{getKYCStatusBadge(shareholder.lexisnexis_status || 'pending')}</div>
                      </div>
                    ) : null}
                  </div>

                  {activeIndex === index ? (
                    <div className="mt-6 space-y-6 border-t border-gray-200 pt-6">
                      <div>
                        <h4 className="mb-3 text-base font-semibold text-gray-800">{t('labels.lblAddress1')}</h4>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                          <DetailField label={t('labels.lblAddress1')} value={address(shareholder)?.address} />
                          <DetailField label={t('labels.lblAddress2')} value={address(shareholder)?.complement} />
                          <DetailField label={t('labels.lblPostalCode')} value={address(shareholder)?.postal_code} />
                          <DetailField label={t('labels.lblCity')} value={address(shareholder)?.city} />
                          <DetailField label={t('labels.lblState')} value={address(shareholder)?.state} />
                          <DetailField label={t('labels.lblNeighborhood')} value={address(shareholder)?.neighborhood} />
                          <DetailField label={t('labels.lblCountry')} value={address(shareholder)?.country} />
                          <DetailField
                            label={t('labels.lblAddressProofOwner')}
                            value={
                              proofOwner === 'other'
                                ? t('labels.lblAddressProofOwnerOther')
                                : proofOwner === 'self'
                                  ? t('labels.lblAddressProofOwnerSelf')
                                  : undefined
                            }
                          />
                          {proofOwner === 'other' ? (
                            <DetailField
                              label={t('labels.lblKinshipDegree')}
                              value={
                                address(shareholder)?.relationship_degree ||
                                address(shareholder)?.meta_data?.address_proof_kinship
                              }
                            />
                          ) : null}
                        </div>
                      </div>

                      <div>
                        <h4 className="mb-3 text-base font-semibold text-gray-800">{t('labels.lblPassport')}</h4>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                          <DetailField label={t('labels.lblPassportNo')} value={passport(shareholder)?.number} />
                          <DetailField
                            label={t('labels.lblExpiryDate') || 'Expiry Date'}
                            value={passport(shareholder)?.expiry_date}
                          />
                          <DetailField
                            label={t('labels.lblPassportIssuingCountry')}
                            value={passport(shareholder)?.issuing_country}
                          />
                          <DetailField
                            label={t('labels.lblIssueDate') || 'Issuance date'}
                            value={passport(shareholder)?.issuing_date}
                          />
                        </div>
                      </div>

                      {isVibileKYCModule ? (
                        <div>
                          <h4 className="mb-3 text-base font-semibold text-gray-800">{t('others.othDetailsKyc')}</h4>
                          <div className="space-y-4">
                            {Array.isArray(shareholder?.lexisnexis_enforcements) &&
                            shareholder.lexisnexis_enforcements.length > 0 ? (
                              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                                <h5 className="mb-3 font-semibold text-amber-800">
                                  {t('KYCDocuments.detailsKYC.Enforcements')}
                                </h5>
                                <div className="space-y-3">
                                  {shareholder.lexisnexis_enforcements.map((enforcement: any, idx: number) => (
                                    <div key={idx} className="rounded-lg border border-amber-200 bg-white p-3 text-sm">
                                      <p>
                                        <span className="font-medium text-gray-600">
                                          {t('KYCDocuments.detailsKYC.Source')}:
                                        </span>{' '}
                                        {enforcement.Source}
                                      </p>
                                      <p>
                                        <span className="font-medium text-gray-600">
                                          {t('KYCDocuments.detailsKYC.Country')}:
                                        </span>{' '}
                                        {enforcement.Country}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : null}

                            {!shareholder?.lexisnexis_enforcements?.length && !shareholder?.lexisnexis_peps?.length ? (
                              <div className="rounded-lg bg-gray-50 p-6 text-center">
                                <p className="font-medium text-gray-600">{t('KYCDocuments.notFound')}</p>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <h3 className="mb-2 text-lg font-semibold text-gray-800">{t('titles.ttlThereNoRecords')}</h3>
            <p className="text-gray-600">
              {t('labels.lblNoDirectorsOrShareholders') || 'Nenhum diretor ou acionista cadastrado ainda.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
